import { defineComponent as _defineComponent } from 'vue'
import { Project, UltimateSubmissionPolicy } from "ag-client-typescript";
import { watch, reactive, computed, ref } from "vue";
import { useRouter } from "vue-router/composables";
import moment from "moment-timezone";
import {
  to_wall_time,
  to_iso,
  use_datetime_model,
} from "@/composables/use_datetime_model";
import APIErrors from "@/components/api_errors.vue";
import { APIErrorsExposed } from "@/exposed_component_types/api_errors_exposed";
import Modal from "@/components/modal.vue";
import Tooltip from "@/components/tooltip.vue";
import NewValidatedForm from "../validated_input/NewValidatedForm.vue";
import ValidatedTextInput from "../validated_input/ValidatedTextInput.vue";
import ValidatedTextAreaInput from "../validated_input/ValidatedTextAreaInput.vue";
import ValidatedIntInput from "../validated_input/ValidatedIntInput.vue";
import ValidatedNullableIntInput from "../validated_input/ValidatedNullableIntInput.vue";
import {
  assert,
  assert_not_null,
  deep_copy,
  format_datetime_short,
  toggle,
} from "@/utils";
import {
  is_not_empty,
  make_min_validator,
  make_nullable_min_validator,
} from "@/new_validators";

type PropTypes = {
  project: Project;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'project_settings',
  props: {
    project: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  project: Project;
};



const api_errors = ref<APIErrorsExposed>();
const delete_errors = ref<APIErrorsExposed>();
const timezone_input = ref<HTMLSelectElement>();

const state = reactive({
  project: deep_copy(props.project, Project),
  saving: false,
  settings_form_is_valid: true,
  show_reset_time_picker: false,
  show_delete_project_modal: false,
  deleting: false,
});

watch(
  () => props.project,
  (project) => {
    state.project = deep_copy(project, Project);
  },
);

const router = useRouter();

const timezones = computed(() => {
  return moment.tz.names();
});

const timezone_model = computed({
  get() {
    return state.project.timezone;
  },
  set(new_zone) {
    const wall_soft_closing_time = to_wall_time(
      state.project.soft_closing_time,
      state.project.timezone,
    );

    // closing_time is only undefined for students, who won't visit this page
    assert(state.project.closing_time !== undefined);
    const wall_closing_time = to_wall_time(
      state.project.closing_time,
      state.project.timezone,
    );

    state.project.timezone = new_zone;

    // Update the underlying ISO time so that the wall times
    // in the form don't change when the timezone changes.

    state.project.soft_closing_time = to_iso(wall_soft_closing_time, new_zone);
    state.project.closing_time = to_iso(wall_closing_time, new_zone);
  },
});

const soft_closing_time_model = use_datetime_model({
  get_iso: () => state.project.soft_closing_time,
  set_iso: (v) => {
    state.project.soft_closing_time = v;
  },
  get_timezone: () => state.project.timezone,
});

const closing_time_model = use_datetime_model({
  get_iso: () => {
    // closing_time is only undefined for students, who won't visit this page
    assert(state.project.closing_time !== undefined);
    return state.project.closing_time;
  },
  set_iso: (v) => {
    state.project.closing_time = v;
  },
  get_timezone: () => state.project.timezone,
});

const save_project_settings = () => {
  return toggle(state, "saving", async () => {
    assert_not_null(state.project);
    try {
      api_errors.value?.clear();
      await state.project.save();
    } catch (error: unknown) {
      api_errors.value?.show_errors_from_response(error);
    }
  });
};

const delete_project = () => {
  return toggle(state, "deleting", async () => {
    assert_not_null(state.project);
    try {
      api_errors.value?.clear();
      await state.project.delete();
      await router.push({
        name: "course_admin",
        params: { course_id: state.project.course.toString() },
        query: { current_tab: "projects" },
      });
    } catch (error: unknown) {
      delete_errors.value?.show_errors_from_response(error);
    }
  });
};

expose({
  state,
  soft_closing_time_model,
});

return { __sfc: true,props, api_errors, delete_errors, timezone_input, state, router, timezones, timezone_model, soft_closing_time_model, closing_time_model, save_project_settings, delete_project, UltimateSubmissionPolicy, APIErrors, Modal, Tooltip, NewValidatedForm, ValidatedTextInput, ValidatedTextAreaInput, ValidatedIntInput, ValidatedNullableIntInput, format_datetime_short, is_not_empty, make_min_validator, make_nullable_min_validator }
}

})